import { Heading } from 'components/Basic/Heading/Heading';
import { useTypedTranslationFunction } from 'hooks/typescript/useTypedTranslationFunction';
import Link from 'next/link';
import { FC } from 'react';
import { BomItemType } from 'types/product';

type ProductDetailBomItemsProps = {
    bomItems: BomItemType[];
};

const formatQuantity = (quantity: number) => (quantity % 1 === 0 ? quantity.toFixed(0) : quantity.toString());

const Thumbnail: FC<{ item: BomItemType }> = ({ item }) => {
    const url = item.product?.images[0]?.sizes[0]?.url;

    return (
        <div className="flex aspect-square h-16 w-16 shrink-0 items-center justify-center rounded-md bg-white p-1">
            {url ? (
                <img
                    src={url}
                    alt={item.componentName}
                    className="block !max-h-14 w-full !max-w-[56px] object-contain"
                />
            ) : (
                <span className="text-xs text-greyDark">—</span>
            )}
        </div>
    );
};

const NameCell: FC<{ item: BomItemType }> = ({ item }) =>
    item.isLinkable && item.product ? (
        <Link href={item.product.slug} passHref>
            <a className="text-primary underline hover:no-underline">{item.componentName}</a>
        </Link>
    ) : (
        <>{item.componentName}</>
    );

export const ProductDetailBomItems: FC<ProductDetailBomItemsProps> = ({ bomItems }) => {
    const t = useTypedTranslationFunction();

    if (bomItems.length === 0) {
        return null;
    }

    return (
        <div>
            <Heading type="h2">{t('Set contents')}</Heading>

            <ul className="m-0 flex list-none flex-col gap-3 p-0 vl:hidden">
                {bomItems.map((item, index) => (
                    <li
                        key={item.articleNumber}
                        className="flex items-start gap-3 rounded-md border border-greyLighter p-3"
                    >
                        <span className="w-6 shrink-0 text-sm text-greyDark">{index + 1}.</span>
                        <Thumbnail item={item} />
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <div className="break-words text-sm">
                                <NameCell item={item} />
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-greyDark">
                                <span>
                                    {t('Code')}: <code>{item.articleNumber}</code>
                                </span>
                                <span>
                                    {t('Quantity')}:{' '}
                                    <strong className="text-dark">
                                        {formatQuantity(item.quantity)} {t('pcs')}
                                    </strong>
                                </span>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            <div className="hidden overflow-x-auto vl:block">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-greyLighter">
                            <th
                                className="px-4 py-3 text-left text-sm font-semibold text-greyDark"
                                aria-label="Index"
                            />
                            <th className="px-4 py-3 text-left text-sm font-semibold text-greyDark">{t('Image')}</th>
                            <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-greyDark">
                                {t('Article number')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-greyDark">
                                {t('Component name')}
                            </th>
                            <th className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-greyDark">
                                {t('Quantity in set')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {bomItems.map((item, index) => (
                            <tr key={item.articleNumber} className="border-b border-greyLighter">
                                <td className="px-4 py-3 text-left align-middle text-sm text-greyDark">{index + 1}</td>
                                <td className="px-4 py-3 align-middle">
                                    <Thumbnail item={item} />
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-left align-middle text-sm">
                                    <code className="text-greyDark">{item.articleNumber}</code>
                                </td>
                                <td className="px-4 py-3 text-left align-middle text-sm">
                                    <NameCell item={item} />
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-right align-middle text-sm font-semibold">
                                    {formatQuantity(item.quantity)} {t('pcs')}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
